import { useCallback, useEffect, useMemo, useState } from "react";
import type { AuthLoginResponse, AuthMethodsAvailable, AuthSessionResponse, AuthUser } from "../types";
import {
  fetchAuthSession,
  finishPasskeyLogin,
  loginPassword,
  logoutSession,
  refreshAuthSession,
  startPasskeyLogin,
  verifyRecoveryCodeChallenge,
  verifyTotpChallenge
} from "../api";
import { base64UrlToBytes, hasPasskeySupport, readAssertionSignCount, toAllowCredentials, toBase64Url } from "../app/passkey";

const DEFAULT_AUTH_METHODS: AuthMethodsAvailable = { password: true, totp: false, recovery: false, passkey: false };

function authErrMessage(e: unknown): string {
  const msg = String((e as any)?.message || e || "").toLowerCase();
  if (msg.includes("(401)")) return "Invalid username or password.";
  if (msg.includes("(429)")) return "Too many login attempts. Try again later.";
  if (msg.includes("network") || msg.includes("failed to fetch")) return "Network error contacting auth service.";
  return "Unable to sign in right now.";
}

function authSessionBootstrapErrMessage(e: unknown): string {
  const msg = String((e as any)?.message || e || "").toLowerCase();
  if (msg.includes("(401)") || msg.includes("(403)")) return "Session unavailable. Please sign in.";
  if (msg.includes("(500)") || msg.includes("(502)") || msg.includes("(503)") || msg.includes("(504)")) {
    return "Authentication service unavailable (server error).";
  }
  if (msg.includes("network") || msg.includes("failed to fetch")) return "Network error contacting auth service.";
  return "Unable to verify auth session right now.";
}

function mfaErrMessage(e: unknown): string {
  const msg = String((e as any)?.message || e || "").toLowerCase();
  if (msg.includes("(401)")) return "Invalid verification code.";
  if (msg.includes("(429)")) return "Too many attempts. Wait before retrying.";
  return "Unable to verify right now.";
}

const SESSION_ESTABLISHMENT_ERROR = "Session unavailable after sign-in. Please retry.";

function passkeyErrMessage(e: unknown): string {
  const msg = String((e as any)?.message || e || "").toLowerCase();
  const name = String((e as any)?.name || "").toLowerCase();
  if (name === "notallowederror" || msg.includes("notallowederror")) return "Passkey prompt cancelled or timed out.";
  if (name === "notsupportederror" || msg.includes("publickeycredential")) return "Passkeys are not supported in this browser.";
  if (msg.includes("(401)")) return "Passkey verification failed.";
  if (msg.includes("(403)")) return "Passkey origin or rpId mismatch.";
  return "Unable to complete passkey flow right now.";
}

function applyAuthResult(payload: AuthLoginResponse | AuthSessionResponse): {
  authenticated: boolean;
  user: AuthUser | null;
  methods: AuthMethodsAvailable;
  sessionExpiresAt: string | null;
} {
  return {
    authenticated: payload.authenticated === true,
    user: (payload as any).user || null,
    methods: (payload as any).methods_available || DEFAULT_AUTH_METHODS,
    sessionExpiresAt: (payload as any).session_expires_at || null
  };
}

export type AuthController = {
  authState: "checking" | "authenticated" | "unauthenticated";
  authUser: AuthUser | null;
  authMethods: AuthMethodsAvailable;
  authError: string | null;
  authSubmitting: boolean;
  passkeySubmitting: boolean;
  mfaSubmitting: boolean;
  stepUpRequested: boolean;
  mfaChallengeId: string | null;
  sessionExpiresAt: string | null;
  signInPassword: (username: string, password: string) => Promise<void>;
  signInPasskey: (username?: string) => Promise<void>;
  verifyTotp: (code: string) => Promise<void>;
  verifyRecovery: (code: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
  clearAuthError: () => void;
  updateAuthMethods: (patch: Partial<AuthMethodsAvailable>) => void;
};

export function useAuthController(): AuthController {
  const [authState, setAuthState] = useState<"checking" | "authenticated" | "unauthenticated">("checking");
  const [authUser, setAuthUser] = useState<AuthUser | null>(null);
  const [authMethods, setAuthMethods] = useState<AuthMethodsAvailable>(DEFAULT_AUTH_METHODS);
  const [authError, setAuthError] = useState<string | null>(null);
  const [authSubmitting, setAuthSubmitting] = useState(false);
  const [passkeySubmitting, setPasskeySubmitting] = useState(false);
  const [mfaSubmitting, setMfaSubmitting] = useState(false);
  const [stepUpRequested, setStepUpRequested] = useState(false);
  const [mfaChallengeId, setMfaChallengeId] = useState<string | null>(null);
  const [sessionExpiresAt, setSessionExpiresAt] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const session = await fetchAuthSession();
        if (cancelled) return;
        const parsed = applyAuthResult(session);
        setAuthMethods(parsed.methods);
        setSessionExpiresAt(parsed.sessionExpiresAt);
        if (parsed.authenticated) {
          setAuthState("authenticated");
          setAuthUser(parsed.user);
          setAuthError(null);
        } else {
          setAuthState("unauthenticated");
          setAuthUser(null);
        }
      } catch (err) {
        if (cancelled) return;
        setAuthState("unauthenticated");
        setAuthUser(null);
        setAuthError(authSessionBootstrapErrMessage(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const applyVerifiedSession = useCallback(async (fallbackError = SESSION_ESTABLISHMENT_ERROR) => {
    try {
      const session = await fetchAuthSession();
      const verified = applyAuthResult(session);
      setAuthMethods(verified.methods);
      setSessionExpiresAt(verified.sessionExpiresAt);
      if (verified.authenticated) {
        setAuthState("authenticated");
        setAuthUser(verified.user);
        setAuthError(null);
        setStepUpRequested(false);
        setMfaChallengeId(null);
        return true;
      }
    } catch {
      // handled below
    }
    setAuthState("unauthenticated");
    setAuthUser(null);
    setStepUpRequested(false);
    setMfaChallengeId(null);
    setAuthError(fallbackError);
    setSessionExpiresAt(null);
    return false;
  }, []);

  const signInPassword = useCallback(async (username: string, password: string) => {
    setAuthSubmitting(true);
    setAuthError(null);
    try {
      const payload = await loginPassword({ username: username.trim().toLowerCase(), password });
      const parsed = applyAuthResult(payload);
      setAuthMethods(parsed.methods);
      setSessionExpiresAt(parsed.sessionExpiresAt);
      if (parsed.authenticated) {
        await applyVerifiedSession();
        return;
      }
      if ((payload as any).mfa_required === true || payload.authenticated === false) {
        setAuthState("unauthenticated");
        setAuthUser(null);
        setStepUpRequested(true);
        setMfaChallengeId(String((payload as any).challenge_id || "") || null);
        setAuthError("Additional verification is required for this account.");
        return;
      }
      setAuthState("unauthenticated");
      setAuthUser(null);
    } catch (err) {
      setAuthState("unauthenticated");
      setAuthUser(null);
      setAuthError(authErrMessage(err));
    } finally {
      setAuthSubmitting(false);
    }
  }, [applyVerifiedSession]);

  const signInPasskey = useCallback(async (username?: string) => {
    if (!hasPasskeySupport()) {
      setAuthError("Passkeys are not supported in this browser.");
      return;
    }
    setPasskeySubmitting(true);
    setAuthError(null);
    try {
      const start = await startPasskeyLogin({ username: username?.trim().toLowerCase() || undefined });
      const assertion = (await navigator.credentials.get({
        publicKey: {
          challenge: base64UrlToBytes(start.challenge) as unknown as BufferSource,
          rpId: start.rp_id,
          allowCredentials: toAllowCredentials(start.allow_credentials),
          userVerification: "preferred"
        }
      })) as PublicKeyCredential | null;
      if (!assertion) throw new Error("passkey assertion cancelled");
      const response = assertion.response as AuthenticatorAssertionResponse;
      const payload = await finishPasskeyLogin({
        challenge_id: start.challenge_id,
        challenge: start.challenge,
        origin: start.origin,
        rp_id: start.rp_id,
        credential_id: toBase64Url(new Uint8Array(assertion.rawId)),
        sign_count: readAssertionSignCount(response.authenticatorData)
      });
      const parsed = applyAuthResult(payload);
      setAuthMethods(parsed.methods);
      setSessionExpiresAt(parsed.sessionExpiresAt);
      if (parsed.authenticated) {
        await applyVerifiedSession();
      } else {
        setAuthState("unauthenticated");
        setAuthUser(null);
        setAuthError("Additional verification is required for this account.");
      }
    } catch (err) {
      setAuthState("unauthenticated");
      setAuthUser(null);
      setAuthError(passkeyErrMessage(err));
    } finally {
      setPasskeySubmitting(false);
    }
  }, [applyVerifiedSession]);

  const verifyTotp = useCallback(async (code: string) => {
    if (!mfaChallengeId || !code.trim()) {
      setAuthError("Enter a TOTP code.");
      return;
    }
    setMfaSubmitting(true);
    setAuthError(null);
    try {
      const payload = await verifyTotpChallenge({ challenge_id: mfaChallengeId, code: code.trim() });
      const parsed = applyAuthResult(payload);
      setAuthMethods(parsed.methods);
      setSessionExpiresAt(parsed.sessionExpiresAt);
      if (parsed.authenticated) {
        await applyVerifiedSession();
      } else {
        setAuthState("unauthenticated");
        setAuthUser(null);
        setStepUpRequested(false);
        setMfaChallengeId(null);
      }
    } catch (err) {
      setAuthError(mfaErrMessage(err));
    } finally {
      setMfaSubmitting(false);
    }
  }, [applyVerifiedSession, mfaChallengeId]);

  const verifyRecovery = useCallback(async (code: string) => {
    if (!mfaChallengeId || !code.trim()) {
      setAuthError("Enter a recovery code.");
      return;
    }
    setMfaSubmitting(true);
    setAuthError(null);
    try {
      const payload = await verifyRecoveryCodeChallenge({ challenge_id: mfaChallengeId, code: code.trim() });
      const parsed = applyAuthResult(payload);
      setAuthMethods(parsed.methods);
      setSessionExpiresAt(parsed.sessionExpiresAt);
      if (parsed.authenticated) {
        await applyVerifiedSession();
      } else {
        setAuthState("unauthenticated");
        setAuthUser(null);
        setStepUpRequested(false);
        setMfaChallengeId(null);
      }
    } catch (err) {
      setAuthError(mfaErrMessage(err));
    } finally {
      setMfaSubmitting(false);
    }
  }, [applyVerifiedSession, mfaChallengeId]);

  const signOut = useCallback(async () => {
    try {
      await logoutSession();
    } catch {
      // no-op
    }
    setAuthState("unauthenticated");
    setAuthUser(null);
    setStepUpRequested(false);
    setMfaChallengeId(null);
    setAuthError(null);
    setSessionExpiresAt(null);
  }, []);

  const refreshSessionNow = useCallback(async () => {
    try {
      const payload = await refreshAuthSession();
      const parsed = applyAuthResult(payload);
      setAuthMethods(parsed.methods);
      setSessionExpiresAt(parsed.sessionExpiresAt);
      setAuthState(parsed.authenticated ? "authenticated" : "unauthenticated");
      setAuthUser(parsed.authenticated ? parsed.user : null);
      if (!parsed.authenticated) {
        setAuthError("Session expired. Please sign in again.");
      }
    } catch {
      setAuthState("unauthenticated");
      setAuthUser(null);
      setAuthError("Session expired. Please sign in again.");
      setSessionExpiresAt(null);
    }
  }, []);

  return useMemo(
    () => ({
      authState,
      authUser,
      authMethods,
      authError,
      authSubmitting,
      passkeySubmitting,
      mfaSubmitting,
      stepUpRequested,
      mfaChallengeId,
      sessionExpiresAt,
      signInPassword,
      signInPasskey,
      verifyTotp,
      verifyRecovery,
      signOut,
      refreshSession: refreshSessionNow,
      clearAuthError: () => setAuthError(null),
      updateAuthMethods: (patch: Partial<AuthMethodsAvailable>) =>
        setAuthMethods((prev) => ({ ...prev, ...patch }))
    }),
    [
      authError,
      authMethods,
      authState,
      authSubmitting,
      authUser,
      mfaChallengeId,
      mfaSubmitting,
      passkeySubmitting,
      refreshSessionNow,
      sessionExpiresAt,
      signInPasskey,
      signInPassword,
      signOut,
      stepUpRequested,
      setAuthMethods,
      verifyRecovery,
      verifyTotp
    ]
  );
}
